import React, {Component} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import {change, Field, reduxForm} from 'redux-form';
import {connect} from 'react-redux';
import {compose} from 'recompose';
import {RESOURCES as reportsResources} from '../fuseReportsResourceMap';
import {
    SelectInput,
} from 'admin-on-rest';
import LocationFilter from './LocationFilter';
import {checkTokenStatus} from '../shared/tokenFunctions';
import {MONTHS, TIME_ZONE_OFFSETS} from '../shared/dateTimeLists';
import {filterStyles} from '../shared/styles-filter';
import Config from '../messages';

/**
 * A form containing a SelectInput component with Mode choices
 *
 */
class SettlementReportsFilter extends Component {
    constructor(props) {
        super(props);
        this.state = {
            zeroDaysList: [],
            displayDateFilter: false,
            errorMessage: null,
            locationsList: [],
            selectedLocation: null,
            selectedTimeZoneOffset: (props.selectedOffset) ? props.selectedOffset : null,
            selectedZeroDay: null,
            zeroDayOptionsList: []
        };
        this.handleDateRangeSelect = this.handleDateRangeSelect.bind(this);
        this.handleLocationSelect = this.handleLocationSelect.bind(this);
        this.handleOffsetSelect = this.handleOffsetSelect.bind(this);
        this.setLocationList = this.setLocationList.bind(this);
    }

    /* API Calls ------------------------------ */

    /**
     * Get Zero Day results for Date Range filter based on selected Location ID
     *
     * @param locationId
     */
    fetchZeroDays(locationId) {

        const params = {
            filter: {},
            location_id: locationId.toString(),
            pagination: {page: 1, perPage: 10000},
            sort: {field: "zero_day", order: "ASC"}
        };
        const resource = reportsResources.ZeroDays.GET_LIST(params);

        // Retrieve the data and assign results to the Select choices
        import('../shared/serverFunctions')
            .then(functions => {

                const tokenStatus = checkTokenStatus(null);
                if (tokenStatus.expired) {
                    return;
                }

                axios({
                    method: 'get',
                    url: resource.uri,
                    headers: functions.getGetHeaders()
                }).then((result) => {
                    if (result.data.payload) {
                        this.processZeroDaysData(result.data.payload);
                    }
                }).catch((error) => {
                    let message = Config.fuse.error.error + ' Zero Days.';
                    if (error.response) {
                        // Server response outside 2xx
                    } else if (error.request) {
                        // No response
                        message = Config.fuse.error.no_response + ' Zero Days.';
                    } else {
                        console.log('Error', error.message);
                    }
                    this.setState({
                        errorMessage: message
                    });
                });
            });
    }

    /* Data Processing ------------------------------ */

    /**
     * Process Zero Day results for dropdown
     *
     * @param list
     */
    processZeroDaysData(list) {
        let dateOptions = [];
        // For Settlement Reports, cycle through all Zero days except the last one
        // "Next" item is reserved for possible visual aid
        for (let i = 0; i < (list.length - 1); i++) {
            const current = list[i];
            const next = list[i + 1];

            if (current && next) {
                const curZD = current.zero_day.toString();
                const nextZD = next.zero_day.toString();
                const curArr = [curZD.substr(0, 4), curZD.substr(4, 2), curZD.substr(6, 2)];
                const nextArr = [nextZD.substr(0, 4), nextZD.substr(4, 2), nextZD.substr(6, 2)];
                const curMonth = MONTHS.find(m => m.id === curArr[1]);
                const nextMonth = MONTHS.find(m => m.id === nextArr[1]);
                const curString = curMonth.name + ' ' + curArr[2] + ', ' + curArr[0];
                const nextString = nextMonth.name + ' ' + nextArr[2] + ', ' + nextArr[0];
                const labelString = curString + ' - ' + nextString;
                dateOptions.push({
                    'id': curZD,
                    'name': labelString,
                    'nextId': nextZD,
                    'nextName': nextString
                });
            }
        }
        this.setState({
            errorMessage: null,
            zeroDaysList: list,
            zeroDayOptionsList: dateOptions,
            displayDateFilter: true
        });
    }

    /**
     * Store Locations sent by Location Filter
     *
     * @param list
     */
    setLocationList(list) {
        this.setState({
            locationsList: list
        });
    }

    /**
     * Handle selection event for Location
     *
     * @param event
     * @param index
     * @param value
     */
    handleLocationSelect(event, index, value) {
        if (index && index !== value) {
            // Extract the full Location object from the selected location_id
            const locationObj = this.state.locationsList.find(r => r.id.toString() === index.toString());
            if (locationObj) {
                this.setState({
                    selectedLocation: locationObj,
                    selectedZeroDay: null,
                    zeroDayOptionsList: []
                });
                this.props.dispatch(change('filterForm', 'location', locationObj));
                this.props.dispatch(change('filterForm', 'zero_day', null));
                this.props.onSelectLocation('location', locationObj);
            }
            this.fetchZeroDays(index);
        }
    }

    /**
     * Handle selection event for zero day range
     *
     * @param event
     * @param index
     * @param value
     */
    handleDateRangeSelect(event, index, value) {
        if (index && index !== value) {
            this.setState({
                selectedZeroDay: index
            });
            this.props.dispatch(change('filterForm', 'zero_day', index));
            this.props.onSelectZeroDay('zero_day', index);
        }
    }

    /**
     * Handle selection event for Time Zone Offset
     *
     * @param event
     * @param index
     * @param value
     */
    handleOffsetSelect(event, index, value) {
        if (index && index !== value) {
            this.setState({
                    selectedTimeZoneOffset: index
                },
                () => {
                    this.props.dispatch(change('filterForm', 'time_zone_offset', index));
                    this.props.onSelectTimeZoneOffset('time_zone_offset', index);
                });
        }
    }

    render() {
        const {
            errorMessage
        } = this.state;

        return (
            <div className="list-filter">
                <p style={filterStyles.fieldInstructions}>Select a Location to begin report generation:</p>
                <LocationFilter
                    label="Select a Location:"
                    onLocationsLoaded={this.setLocationList}
                    onSelectLocation={this.handleLocationSelect}
                    selectionType="id"
                />
                <form>
                    <div className="list-filter">
                        {this.state.displayDateFilter && this.state.zeroDayOptionsList &&
                            <div>
                                <p style={filterStyles.fieldInstructions}>Select a date:</p>
                                <Field name="zero_day"
                                       component={SelectInput}
                                       choices={this.state.zeroDayOptionsList}
                                       onChange={this.handleDateRangeSelect}
                                       elStyle={filterStyles.selectInputWide}
                                />
                                <p style={filterStyles.fieldInstructions}>Select a time zone offset:</p>
                                <Field name="time_zone_offset"
                                       component={SelectInput}
                                       label="Select a Time Zone Offset"
                                       choices={TIME_ZONE_OFFSETS}
                                       onChange={this.handleOffsetSelect}
                                       elStyle={filterStyles.selectInput}
                                       options={{
                                           value: this.props.selectedOffset
                                       }}
                                />
                            </div>
                        }
                        <hr style={filterStyles.break}/>
                    </div>
                </form>
                {/* Error and Messaging */}
                {errorMessage &&
                    <div className="error">
                        <p>{errorMessage}</p>
                    </div>
                }
            </div>
        );
    }
}

const mapStateToProps = state => {
    let filterForm = state.form['filterForm'];
    let timeZoneForm = state.form['timeZoneForm'];
    const defaultOffsetValue = (timeZoneForm && timeZoneForm.values && timeZoneForm.values['timeZoneDefault']) ? timeZoneForm.values['timeZoneDefault'] : null;
    if (filterForm && filterForm.values) {
        const offsetValue = filterForm.values['time_zone_offset'] ? filterForm.values['time_zone_offset'].toString() : defaultOffsetValue;
        return {
            selectedOffset: offsetValue
        };
    }
    return {
        selectedOffset: defaultOffsetValue
    };
};

SettlementReportsFilter.propTypes = {
    addField: PropTypes.bool,
    basePath: PropTypes.string,
    onSelectLocation: PropTypes.func,
    onSelectTimeZoneOffset: PropTypes.func,
    onSelectZeroDay: PropTypes.func,
    submitOnEnter: PropTypes.bool,
};

SettlementReportsFilter.defaultProps = {
    addField: true,
    submitOnEnter: false,
};

const enhance = compose(
    connect(mapStateToProps),
    reduxForm({
        form: 'filterForm',
        enableReinitialize: true,
    })
);

export default enhance(SettlementReportsFilter);



// WEBPACK FOOTER //
// ./src/Fuse/components/SettlementReportsFilter.js